/**
 * Track our Page Performance metrics for page load and navigation.
 */
import * as React from "react";
import {useLocation as useLocationV6} from "react-router-dom-v5-compat";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {GoogleAnalytics} from "@khan/analytics-google";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    capturePagePerformance,
    PageLifecycle,
} from "@khan/analytics-performance";
import type {Entrypoint} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";

import {useRoutePath} from "./use-route-path.ts";

/**
 * Track our Page Performance metrics for page load and navigation.
 *
 * This calls both our web-vitals and our custom page performance metrics.
 * It attempts to make sure that we capture the metrics at the right time
 * and without losing data.
 */
export const useTrackPagePerformance = (entrypoint: Entrypoint) => {
    const {key} = useLocationV6();
    const location = useLocation();
    const routerPath = useRoutePath();
    // The initial value is "" so that we capture the first route.
    const pathname = React.useRef("");

    // Per the jsdoc, key "is always 'default' on the initial location."
    const isInitialPageLoad = !key || key === "default";

    // Log the page performance metrics.
    const handleCapturePagePerformance: () => void =
        React.useCallback((): void => {
            capturePagePerformance(routerPath, entrypoint);
        }, [routerPath, entrypoint]);

    // Handle cases where the page has been closed or hidden, we want to log
    // our metrics at that time.
    const handleBackgroundCapturePagePerformance: () => void =
        React.useCallback((): void => {
            if (document.visibilityState === "hidden") {
                handleCapturePagePerformance();
            }
        }, [handleCapturePagePerformance]);

    // We have a fallback where we will log the page performance metrics
    // after 15 seconds if the page hasn't been backgrounded or unloaded.
    const delayedCapture = useTimeout(handleCapturePagePerformance, 15000);

    // Only run during the initial page load.
    useOnMountEffect(() => {
        // Report all available metrics whenever the page is backgrounded or unloaded.
        document.addEventListener(
            "visibilitychange",
            handleBackgroundCapturePagePerformance,
        );

        // NOTE: Safari does not reliably fire the `visibilitychange` event when the
        // page is being unloaded.
        window.addEventListener("pagehide", handleCapturePagePerformance);

        // Start the timer to log the page performance metrics after 15 seconds.
        delayedCapture.set();

        return () => {
            // Remove listeners to ensure that we clean up after ourselves.
            delayedCapture.clear();
            document.removeEventListener(
                "visibilitychange",
                handleBackgroundCapturePagePerformance,
            );
            window.removeEventListener(
                "pagehide",
                handleCapturePagePerformance,
            );
        };
    });

    // Run on every page load and navigation.
    React.useEffect(() => {
        if (!isInitialPageLoad) {
            // If doing a client-side navigation then we want to force whatever
            // page performance data that we have to be logged.
            handleCapturePagePerformance();

            // We don't want to mark this as client side nav if it's the initial
            // page load.  We don't bother checking mounted b/c we need to run
            // this even if LoadableRouteHandler has been unmounted otherwise we'll
            // miss collecting page timing analytics in some cases.
            PageLifecycle.handleStartRouterNavigation();

            // We only want to trigger these analytics events when the pathname
            // changes.
            if (pathname.current !== location.pathname) {
                publishAnalyticsEventForMinorConversion({
                    eventType: "WebAppPageView",
                    eventSchemaVersion: 5,
                    ...PageLifecycle.getPageViewTraitData(location, entrypoint),
                });
                PageLifecycle.markNavConversions(
                    location.pathname,
                    location.search,
                );
                PageLifecycle.checkMarkHomepageConversion(location.pathname);

                GoogleAnalytics.sendToGA("pageview", location.pathname);
                pathname.current = location.pathname;
            }
        }
    }, [location, isInitialPageLoad, handleCapturePagePerformance, entrypoint]);
};
